import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FieldLabel } from '@puckeditor/core';
import {
  FormGroup, Input, Alert, Button,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { showModalTop } from '../actions';

import { APIgetFilePreview } from '../api/Files';

import PuckGrid from '../components/Page/Components/PuckGrid';
import PuckDivider from '../components/Page/Components/PuckDivider';
import PuckHeading from '../components/Page/Components/PuckHeading';
import PuckCollection from '../components/Page/Components/PuckCollection/PuckCollection';
import PuckSlider from '../components/Page/Components/PuckSlider/PuckSlider';
import AdminLogoUploader from '../components/Admin/LogoUploader';
import PuckHTML from '../components/Page/Components/PuckHTML';
import PuckColors from '../components/Page/Components/PuckColors/PuckColors';
import PuckFile from '../components/Page/Components/PuckFile';
import PuckCollectionFeed from '../components/Page/Components/PuckCollectionFeed/PuckCollectionFeed';
import PuckVideo from '../components/Page/Components/PuckVideo';
import PuckFonts from '../components/Page/Components/PuckFonts/PuckFonts';
import PuckHero from '../components/Page/Components/PuckHero';
import PuckButton from '../components/Page/Components/PuckButton/PuckButton';
import PuckNavigation from '../components/Page/Components/PuckNavigation';
import PageNavigationItemsField from '../components/Page/Components/Common/PageNavigationItemsField';
import AccessRestrictionItemsField, { DEFAULT_ACCESS_RESTRICTION } from '../components/Page/Components/Common/AccessRestrictionItemsField';
import FontPickerField from '../components/Page/Components/Common/FontPickerField';
import RadioField from '../components/Page/Components/Common/RadioField';
import ColorPickerField from '../components/Page/Components/Common/ColorPickerField';
import { BgField, resolveBg } from '../components/Page/Components/PuckLayout';
import { buildPageFontPresentation } from './PageFonts';

import { getTransparentHeaderPaddingClass, normalizePageVisibilityMode } from './PageSections';

const DEFAULT_PAGE_ACCENT_COLOR = '#203444';

const isValidHexColor = (value) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(`${value || ''}`);

const normalizeAccentColorValue = (value) => {
  const workspacePrimary = (typeof window !== 'undefined'
    ? getComputedStyle(document.documentElement).getPropertyValue('--primary').trim()
    : '') || DEFAULT_PAGE_ACCENT_COLOR;

  const defaultColor = isValidHexColor(workspacePrimary)
    ? workspacePrimary
    : DEFAULT_PAGE_ACCENT_COLOR;

  if (value && typeof value === 'object') {
    return {
      mode: value.mode === 'custom' ? 'custom' : 'workspace',
      color: isValidHexColor(value.color) ? value.color : defaultColor,
    };
  }

  if (isValidHexColor(value)) {
    return {
      mode: 'custom',
      color: value,
    };
  }

  return {
    mode: 'workspace',
    color: defaultColor,
  };
};

const LogoField = ({ value, onChange, field }) => {
  const intl = useIntl();
  const [previewUrl, setPreviewUrl] = useState(value || null);

  const handleRemove = () => {
    onChange('');
    setPreviewUrl(null);
  };

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return;
    }

    if (/^https?:\/\//i.test(value)) {
      setPreviewUrl(value);
      return;
    }

    APIgetFilePreview(value, { full: true })
      .then((res) => {
        if (res.data?.url) {
          setPreviewUrl(res.data.url);
          return;
        }

        setPreviewUrl(null);
      })
      .catch(() => {
        setPreviewUrl(null);
      });
  }, [value]);

  return (
    <FieldLabel label={field.label} className="puck-logo-field-label">
      <FormGroup className="logo-uploader" style={{ marginTop: 4 }}>
        {previewUrl && (
          <div style={{ marginBottom: 8 }}>
            <img src={previewUrl} alt="Logo" style={{ maxHeight: 48, maxWidth: '100%' }} />
          </div>
        )}
        <AdminLogoUploader
          name="puck-logo-uploader"
          uploadButtonText={previewUrl ? 'Change' : 'Upload'}
          handleChangeImage={(url) => {
            onChange(url);
          }}
          handleChangePreview={(url) => {
            setPreviewUrl(url);
          }}
        />
        {previewUrl && (
          <Button size="sm" color="secondary" onClick={handleRemove} className="w-100 mt-1">
            {intl.formatMessage({ defaultMessage: 'Remove' })}
          </Button>
        )}
      </FormGroup>
    </FieldLabel>
  );
};

LogoField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({ label: PropTypes.string }).isRequired,
};

LogoField.defaultProps = {
  value: '',
};

const FontField = ({
  value,
  onChange,
  field,
  onShowModalTop,
  hint,
}) => (
  <FieldLabel label={field.label} className="puck-page-font-picker-label">
    {hint && (
      <div className="puck-common-field-hint">
        {hint}
      </div>
    )}
    <div className="puck-fonts-field-item puck-page-font-picker-item">
      <FontPickerField
        value={value}
        onChange={onChange}
        hint={hint}
        onOpenFilePicker={(handleFileData) => {
          onShowModalTop('FileSelectModalEnhanced', {
            className: 'modal-select-file modal-select-file-enhanced',
            handleSelectFile: () => {},
            handleFileUrl: () => {},
            handleFileData,
            allowUpload: true,
            allowedFileExtensions: ['ttf', 'otf', 'woff', 'woff2'],
            showThumbnail: false,
          });
        }}
      />
    </div>
  </FieldLabel>
);

FontField.propTypes = {
  hint: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({ label: PropTypes.string }).isRequired,
  onShowModalTop: PropTypes.func.isRequired,
};

FontField.defaultProps = {
  value: '',
};

const mapDispatchToProps = (dispatch) => ({
  onShowModalTop: (modalName, params) => dispatch(showModalTop(modalName, params)),
});

const ConnectedFontField = connect(null, mapDispatchToProps)(FontField);

const VisibilityField = ({ value, onChange, field }) => {
  const intl = useIntl();
  const mode = normalizePageVisibilityMode(value);

  const isLoginRequired = mode === 'login_required';
  const isPublic = mode === 'public';
  const isHomepage = mode === 'homepage';

  return (
    <FieldLabel label={field.label} className="puck-visibility-field-label">
      <Input
        type="select"
        value={mode}
        onChange={e => onChange(e.target.value)}
        style={{ marginTop: 4 }}
      >
        <option value="login_required">{intl.formatMessage({ defaultMessage: 'Login required' })}</option>
        <option value="public">{intl.formatMessage({ defaultMessage: 'Public' })}</option>
        <option value="homepage">{intl.formatMessage({ defaultMessage: 'Landing page' })}</option>
      </Input>
      {isLoginRequired && (
        <Alert color="warning" size="sm" className="mt-2 puck-visibility-field-alert">
          {intl.formatMessage({ defaultMessage: 'A workspace account is required to view and access this Content Portal.' })}
        </Alert>
      )}
      {isPublic && (
        <Alert color="danger" size="sm" className="mt-2 puck-visibility-field-alert">
          {intl.formatMessage({ defaultMessage: 'This page will be publicly available. All files and Collections used will be viewable and downloadable.' })}
        </Alert>
      )}
      {isHomepage && (
        <Alert color="warning" size="sm" className="mt-2 puck-visibility-field-alert">
          {intl.formatMessage({ defaultMessage: 'Content Portal is viewable, but a log in is required to view or download content' })}
        </Alert>
      )}
    </FieldLabel>
  );
};

VisibilityField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({ label: PropTypes.string }).isRequired,
};

VisibilityField.defaultProps = {
  value: 'login_required',
};

const AccentColorField = ({ value, onChange, field }) => {
  const intl = useIntl();
  const normalized = normalizeAccentColorValue(value);
  const isCustom = normalized.mode === 'custom';

  const handleModeChange = (mode) => {
    onChange({
      ...normalized,
      mode,
    });
  };

  return (
    <div className="puck-accent-field-label">
      <RadioField
        id="page-accent-mode"
        name="pageAccentMode"
        label={field.label}
        value={normalized.mode}
        onChange={handleModeChange}
        options={[
          { label: intl.formatMessage({ defaultMessage: 'Workspace color' }), value: 'workspace' },
          { label: intl.formatMessage({ defaultMessage: 'Custom' }), value: 'custom' },
        ]}
      />
      {isCustom && (
        <FieldLabel
          label={intl.formatMessage({ defaultMessage: 'Custom accent color' })}
          className="mt-4"
        >
          <ColorPickerField
            value={normalized.color}
            onChange={nextColor => onChange({ mode: 'custom', color: nextColor })}
            showLabel={false}
            defaultColor={DEFAULT_PAGE_ACCENT_COLOR}
          />
        </FieldLabel>
      )}
    </div>
  );
};

const PageNavigationField = ({
  value,
  onChange,
  field,
  readOnly,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="puck-section-heading-field">
      <Button
        color="link"
        className="puck-section-heading-field-toggle"
        onClick={() => setIsOpen(prev => !prev)}
        disabled={readOnly}
      >
        <span className="puck-section-heading-field-toggle-label">{field.label}</span>
        <i className={`far ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
      </Button>

      {isOpen && (
        <div className="puck-section-heading-field-body">
          <PageNavigationItemsField value={value} onChange={onChange} />
        </div>
      )}
    </div>
  );
};

PageNavigationField.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
  readOnly: PropTypes.bool,
};

PageNavigationField.defaultProps = {
  value: [],
  readOnly: false,
};

AccentColorField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({ label: PropTypes.string }).isRequired,
};

AccentColorField.defaultProps = {
  value: null,
};

const AccessRestrictionField = ({
  value,
  onChange,
  field,
  readOnly,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="puck-section-heading-field">
      <Button
        color="link"
        className="puck-section-heading-field-toggle"
        onClick={() => setIsOpen(prev => !prev)}
        disabled={readOnly}
      >
        <span className="puck-section-heading-field-toggle-label">{field.label}</span>
        <i className={`far ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
      </Button>

      {isOpen && (
        <div className="puck-section-heading-field-body">
          <AccessRestrictionItemsField value={value} onChange={onChange} />
        </div>
      )}
    </div>
  );
};

AccessRestrictionField.propTypes = {
  value: PropTypes.shape({
    requireName: PropTypes.bool,
    requireEmail: PropTypes.bool,
    requirePassword: PropTypes.bool,
    password: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({ label: PropTypes.string }).isRequired,
  readOnly: PropTypes.bool,
};

AccessRestrictionField.defaultProps = {
  value: DEFAULT_ACCESS_RESTRICTION,
  readOnly: false,
};

export const getPuckConfig = (intl) => ({
  root: {
    label: intl.formatMessage({ defaultMessage: 'Page settings' }),
    fields: {
      name: {
        type: 'text',
        label: 'Page name',
      },
      url: {
        type: 'text',
        label: 'Page URL',
      },
      pageNavigation: {
        type: 'custom',
        label: intl.formatMessage({ defaultMessage: 'Page navigation' }),
        render: (props) => <PageNavigationField {...props} />,
      },
      access_restriction: {
        type: 'custom',
        label: intl.formatMessage({ defaultMessage: 'Access restriction' }),
        render: (props) => <AccessRestrictionField {...props} />,
      },
      visibility_mode: {
        type: 'custom',
        label: intl.formatMessage({ defaultMessage: 'Visibility' }),
        render: (props) => <VisibilityField {...props} />,
      },
      page_type: {
        type: 'radio',
        label: intl.formatMessage({ defaultMessage: 'Page type' }),
        options: [
          { label: intl.formatMessage({ defaultMessage: 'Full width' }), value: 'fullscreen' },
          { label: intl.formatMessage({ defaultMessage: 'With sidebar' }), value: 'internal' },
        ],
      },
      header: {
        type: 'radio',
        label: intl.formatMessage({ defaultMessage: 'Header type' }),
        options: [
          { label: intl.formatMessage({ defaultMessage: 'Transparent' }), value: 'transparent' },
          { label: intl.formatMessage({ defaultMessage: 'Solid' }), value: 'solid' },
        ],
      },
      showSearchBar: {
        type: 'radio',
        label: intl.formatMessage({ defaultMessage: 'Show search bar' }),
        options: [
          { label: intl.formatMessage({ defaultMessage: 'Yes' }), value: true },
          { label: intl.formatMessage({ defaultMessage: 'No' }), value: false },
        ],
      },
      accentColor: {
        type: 'custom',
        label: intl.formatMessage({ defaultMessage: 'Accent color' }),
        render: (props) => <AccentColorField {...props} />,
      },
      logo_url: {
        type: 'custom',
        label: intl.formatMessage({ defaultMessage: 'Logo' }),
        render: (props) => <LogoField {...props} />,
      },
      fontPrimary: {
        type: 'custom',
        label: intl.formatMessage({ defaultMessage: 'Primary font' }),
        render: (props) => <ConnectedFontField {...props} hint={intl.formatMessage({ defaultMessage: 'Used for all section heading title copy.' })} />,
      },
      fontSecondary: {
        type: 'custom',
        label: intl.formatMessage({ defaultMessage: 'Secondary font' }),
        render: (props) => <ConnectedFontField {...props} hint={intl.formatMessage({ defaultMessage: 'Used for all section heading subtitle copy.' })} />,
      },
      // eslint-disable-next-line no-underscore-dangle
      _bg: {
        type: 'custom',
        label: intl.formatMessage({ defaultMessage: 'Page background' }),
        render: (props) => <BgField {...props} />,
      },
    },
    defaultProps: {
      name: '',
      url: '',
      pageNavigation: [],
      access_restriction: {},
      visibility_mode: 'login_required',
      page_type: 'fullscreen',
      header: 'solid',
      showSearchBar: true,
      accentColor: {
        mode: 'workspace',
        color: DEFAULT_PAGE_ACCENT_COLOR,
      },
      logo_url: '',
      fontPrimary: '',
      fontSecondary: '',
      // eslint-disable-next-line no-underscore-dangle
      _bg: {
        type: 'none', color: '#ffffff', gradientStart: '#ffffff', gradientEnd: '#000000', gradientDeg: 135,
      },
    },
    // eslint-disable-next-line no-underscore-dangle
    render: ({
      children,
      // eslint-disable-next-line no-underscore-dangle
      _bg,
      fontPrimary,
      fontSecondary,
      accentColor,
      header,
      disableTransparentHeaderPadding,
      showSearchBar = true,
    }) => {
      // eslint-disable-next-line no-underscore-dangle
      const background = resolveBg(_bg);
      const { fontStyles, pageFontVars } = buildPageFontPresentation({
        primaryValue: fontPrimary,
        secondaryValue: fontSecondary,
      });
      const normalizedAccentColor = normalizeAccentColorValue(accentColor);
      const useCustomAccent = normalizedAccentColor.mode === 'custom';

      const rootStyle = {
        ...(background ? { background } : {}),
        ...pageFontVars,
        ...(useCustomAccent ? { '--page-accent-color': normalizedAccentColor.color } : {}),
      };

      const transparentHeaderPaddingClass = getTransparentHeaderPaddingClass(
        header,
        disableTransparentHeaderPadding,
        showSearchBar !== false,
      );

      return (
        <div className={`landing-content page-header-${header} ${useCustomAccent ? ' page-accent-custom' : ''}${transparentHeaderPaddingClass}`} style={rootStyle}>
          {fontStyles.length > 0 && <style>{fontStyles.join('\n')}</style>}
          { children }
        </div>
      );
    },
  },
  categories: {
    layout: {
      components: ['Grid', 'Divider', 'Navigation'],
      title: intl.formatMessage({ defaultMessage: 'Layout' }),
    },
    typography: {
      components: ['Heading', 'Hero', 'Button'],
      title: intl.formatMessage({ defaultMessage: 'Typography' }),
    },
    media: {
      components: ['Collection', 'Slider', 'File', 'CollectionFeed', 'Video'],
      title: intl.formatMessage({ defaultMessage: 'Media' }),
    },
    brand: {
      components: ['Fonts', 'Colors'],
      title: intl.formatMessage({ defaultMessage: 'Brand' }),
    },
  },
  components: {
    Grid: {
      ...PuckGrid,
      label: intl.formatMessage({ defaultMessage: 'Columns' }),
    },
    Divider: {
      ...PuckDivider,
      label: intl.formatMessage({ defaultMessage: 'Divider' }),
    },
    Heading: {
      ...PuckHeading,
      label: intl.formatMessage({ defaultMessage: 'Heading' }),
    },
    Collection: {
      ...PuckCollection,
      label: intl.formatMessage({ defaultMessage: 'Collections' }),
    },
    Slider: {
      ...PuckSlider,
      label: intl.formatMessage({ defaultMessage: 'Collection Slider' }),
    },
    HTML: {
      ...PuckHTML,
      label: intl.formatMessage({ defaultMessage: 'HTML' }),
    },
    Colors: {
      ...PuckColors,
      label: intl.formatMessage({ defaultMessage: 'Brand Colors' }),
    },
    Fonts: {
      ...PuckFonts,
      label: intl.formatMessage({ defaultMessage: 'Fonts' }),
    },
    File: {
      ...PuckFile,
      label: intl.formatMessage({ defaultMessage: 'File' }),
    },
    CollectionFeed: {
      ...PuckCollectionFeed,
      label: intl.formatMessage({ defaultMessage: 'File Feed' }),
    },
    Video: {
      ...PuckVideo,
      label: intl.formatMessage({ defaultMessage: 'Video' }),
    },
    Hero: {
      ...PuckHero,
      label: intl.formatMessage({ defaultMessage: 'CTA' }),
    },
    Button: {
      ...PuckButton,
      label: intl.formatMessage({ defaultMessage: 'Button' }),
    },
    Navigation: {
      ...PuckNavigation,
      label: intl.formatMessage({ defaultMessage: 'Navigation' }),
    },
  },
});
